import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs/Subscription';
import { JhiAlertService, JhiDataUtils, JhiEventManager } from 'ng-jhipster';

import { AlertUtils, FileUtils, ParamUtils, deepCopy, scrollToTop } from '../../../shared';
import { QueueService, Principal } from '../../../services';
import { Queue } from '../../../models';
import { QUEUE_LIST_MODIFICATION } from '../../../shared/constants';
import { FileInputComponent } from '../../generic';

@Component({
    selector: 'qms-queue-edit',
    templateUrl: './queue-edit.component.html'
})
export class QueueEditComponent implements OnInit {

    public maxRegistrationPeriod = Queue.MAX_REGISTRATION_PERIOD;
    public minRegistrationPeriod = Queue.MIN_REGISTRATION_PERIOD;
    public maxRegistrationInterval = Queue.MAX_REGISTRATION_INTERVAL;
    public minRegistrationInterval = Queue.MIN_REGISTRATION_INTERVAL;
    public minLengthInfoToPrint = Queue.MIN_LENGTH_INFO_TO_PRINT;
    public maxLengthInfoToPrint = Queue.MAX_LENGTH_INFO_TO_PRINT;
    public minLengthLabelPrefix = Queue.MIN_LENGTH_LABEL_PREFIX;
    public maxLengthLabelPrefix = Queue.MAX_LENGTH_LABEL_PREFIX;
    public queueLogoMaxSize = Queue.MAX_LOGO_SIZE_KB;

    public queue: Queue;
    public initialQueue: Queue;
    public categories: Queue[];
    public queueHasChildren: boolean;
    public isSaving: boolean;

    private routeSubscription: Subscription;
    private currentAccount: Account;

    @ViewChild(FileInputComponent)
    private fileInput: FileInputComponent;

    constructor(private principal: Principal,
                private route: ActivatedRoute,
                private alertService: JhiAlertService,
                private dataUtils: JhiDataUtils,
                private eventManager: JhiEventManager,
                private queueService: QueueService) {
        this.isSaving = false;
        this.queueHasChildren = false;
        this.categories = [];
    }

    ngOnInit() {
        this.routeSubscription = this.route.params.subscribe((params) => {
            ParamUtils.parseNumber(params['id'], (queueId: number) => {
                this.principal.identity(true).then((account: Account) => {
                    this.currentAccount = account;
                    this.load(queueId);
                });
            }, () => {
                AlertUtils.notifyNotFound(this.eventManager, this.alertService);
                this.cancel();
            });
        });
    }

    private load(id: number) {
        this.queueService.find(id).subscribe((queue: Queue) => {
            if (this.principal.hasAccessToFaculty(queue.facultyId)) {
                this.queue = queue;
                this.initialQueue = deepCopy(this.queue);

                this.queueService.findChildren(queue.id).subscribe((children: Queue[]) => {
                    this.queueHasChildren = (children.length !== 0);
                });
                this.queueService.all().subscribe((queues: Queue[]) => {
                    this.categories = queues.filter((queueCategoryOrNot: Queue) => {
                        return queueCategoryOrNot.category === true &&
                            queueCategoryOrNot.id !== this.queue.id &&
                            queueCategoryOrNot.facultyId === this.queue.facultyId;
                    });
                });
            } else {
                AlertUtils.notifyAccessDenied(this.eventManager, this.alertService);
                this.cancel();
            }
        }, () => {
            this.cancel();
        });
    }

    public onRegistrationCheckboxChanged(checkboxValue: boolean) {
        if (checkboxValue === true) {
            this.queue.registration = 'HIDDEN';
        } else if (this.initialQueue.registration !== 'HIDDEN') {
            this.queue.registration = this.initialQueue.registration;
        } else {
            this.queue.registration = Queue.DEFAULT_REGISTRATION_STATE;
        }
    }

    public cannotEditType(): boolean {
        return this.queue.category === true && this.queueHasChildren === true;
    }

    public save() {
        this.isSaving = true;
        this.queueService.update(this.queue).subscribe((response: Queue) => {
            this.onUpdateSuccess(response);
        }, () => {
            this.isSaving = false;
            scrollToTop();
        });
    }

    public cancel() {
        window.history.back();
    }

    public disableQueue() {
        const copy: Queue = deepCopy(this.initialQueue);
        copy.state = 'DISABLED';
        this.isSaving = true;
        this.queueService.update(copy).subscribe((response: Queue) => {
            this.queue = response;
            this.initialQueue = deepCopy(this.queue);
            AlertUtils.notifySuccess(this.eventManager, this.alertService,
                'queue.wasDisabled', { param: response.name });
            this.eventManager.broadcast({ name: QUEUE_LIST_MODIFICATION, content: 'OK' });
            this.isSaving = false;
            scrollToTop();
        }, () => {
            this.isSaving = false;
            this.queue = deepCopy(this.initialQueue);
            scrollToTop();
        });
    }

    public enableQueue() {
        const copy: Queue = deepCopy(this.initialQueue);
        copy.state = Queue.DEFAULT_QUEUE_STATE;
        this.isSaving = true;
        this.queueService.update(copy).subscribe((response: Queue) => {
            this.queue = response;
            this.initialQueue = deepCopy(this.queue);
            AlertUtils.notifySuccess(this.eventManager, this.alertService,
                'queue.wasEnabled', { param: response.name });
            this.eventManager.broadcast({ name: QUEUE_LIST_MODIFICATION, content: 'OK' });
            this.isSaving = false;
            scrollToTop();
        }, () => {
            this.isSaving = false;
            this.queue = deepCopy(this.initialQueue);
            scrollToTop();
        });
    }

    public setFileData(event: any) {
        FileUtils.validateImageSize(event, Queue.MIM_LOGO_WIDTH, Queue.MIM_LOGO_HEIGHT, () => {
            FileUtils.setFileData(event, this.dataUtils, (fileData: any, fileType: string) => {
                this.queue.logo = fileData;
                this.queue.logoContentType = fileType;
            });
        }, (errorMessage: string, params?: any) => {
            this.fileInput.clearFile();
            AlertUtils.notifyError(this.eventManager, this.alertService, errorMessage, params);
        });
    }

    public clearInputImage() {
        this.queue.logoContentType = null;
        this.queue.logo = null;
    }

    private onUpdateSuccess(result: Queue) {
        AlertUtils.notifySuccess(this.eventManager, this.alertService,
            'queue.updated', { param: result.name });

        this.eventManager.broadcast({ name: QUEUE_LIST_MODIFICATION, content: 'OK' });
        this.isSaving = false;
        this.cancel();
    }

    public getLogo(queue: Queue) {
        return FileUtils.getImageBase64Format(queue.logo, queue.logoContentType);
    }

    public trackQueueById(index: number, item: Queue) {
        return item.id;
    }
}



// WEBPACK FOOTER //
// ./src/main/webapp/app/components/management/queues/queue-edit.component.ts